/**
 * Datetime presets utility for SimpleTable filters
 *
 * Provides common date range presets for filtering datetime columns
 * Uses date-fns for accurate date calculations
 */

import {
  format,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  subDays,
  addDays,
  addMonths,
  addYears,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  subWeeks,
  addWeeks,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  addQuarters,
} from "date-fns";

/** Filter sentinel: maps to GraphQL `{ last_contacted_at: { is: "NULL" } }` (no recorded communications). */
export const NEVER_LAST_ACTIVITY_PRESET_VALUE = "never";

/** User-facing label for {@link NEVER_LAST_ACTIVITY_PRESET_VALUE}. */
export const NEVER_LAST_ACTIVITY_PRESET_LABEL = "Never contacted";

export interface DatetimePreset {
  label: string;
  value: string;
  getDateRange: () => { start: Date; end: Date };
  description?: string;
}

/**
 * Get all available datetime presets
 */
export const getDatetimePresets = (): DatetimePreset[] => [
  // Today/Tomorrow/Yesterday
  {
    label: "Today",
    value: "today",
    description: "From midnight to now",
    getDateRange: () => {
      const today = new Date();
      return { start: startOfDay(today), end: endOfDay(today) };
    },
  },
  {
    label: "Tomorrow",
    value: "tomorrow",
    description: "Full day tomorrow",
    getDateRange: () => {
      const tomorrow = addDays(new Date(), 1);
      return { start: startOfDay(tomorrow), end: endOfDay(tomorrow) };
    },
  },
  {
    label: "Yesterday",
    value: "yesterday",
    description: "Full day yesterday",
    getDateRange: () => {
      const yesterday = subDays(new Date(), 1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    },
  },

  // Day ranges
  {
    label: "Last 7 days",
    value: "last7days",
    description: "Past 7 days including today",
    getDateRange: () => {
      const end = endOfDay(new Date());
      const start = startOfDay(subDays(end, 6));
      return { start, end };
    },
  },
  {
    label: "Last 14 days",
    value: "last14days",
    description: "Past 14 days including today",
    getDateRange: () => {
      const end = endOfDay(new Date());
      const start = startOfDay(subDays(end, 13));
      return { start, end };
    },
  },
  {
    label: "Last 30 days",
    value: "last30days",
    description: "Past 30 days including today",
    getDateRange: () => {
      const end = endOfDay(new Date());
      const start = startOfDay(subDays(end, 29));
      return { start, end };
    },
  },
  {
    label: "Last 60 days",
    value: "last60days",
    description: "Past 60 days including today",
    getDateRange: () => {
      const end = endOfDay(new Date());
      const start = startOfDay(subDays(end, 59));
      return { start, end };
    },
  },
  {
    label: "Last 90 days",
    value: "last90days",
    description: "Past 90 days including today",
    getDateRange: () => {
      const end = endOfDay(new Date());
      const start = startOfDay(subDays(end, 89));
      return { start, end };
    },
  },

  // Weekly periods
  {
    label: "This week",
    value: "thisweek",
    description: "Monday to Sunday of current week",
    getDateRange: () => {
      const now = new Date();
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
      };
    },
  },
  {
    label: "Last week",
    value: "lastweek",
    description: "Monday to Sunday of previous week",
    getDateRange: () => {
      const lastWeek = subWeeks(new Date(), 1);
      return {
        start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        end: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      };
    },
  },
  {
    label: "Next week",
    value: "nextweek",
    description: "Monday to Sunday of next week",
    getDateRange: () => {
      const nextWeek = addWeeks(new Date(), 1);
      return {
        start: startOfWeek(nextWeek, { weekStartsOn: 1 }),
        end: endOfWeek(nextWeek, { weekStartsOn: 1 }),
      };
    },
  },

  // Monthly periods
  {
    label: "This month",
    value: "thismonth",
    description: "First to last day of current month",
    getDateRange: () => {
      const now = new Date();
      return { start: startOfMonth(now), end: endOfMonth(now) };
    },
  },
  {
    label: "Last month",
    value: "lastmonth",
    description: "First to last day of previous month",
    getDateRange: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    },
  },
  {
    label: "Next month",
    value: "nextmonth",
    description: "First to last day of next month",
    getDateRange: () => {
      const nextMonth = addMonths(new Date(), 1);
      return { start: startOfMonth(nextMonth), end: endOfMonth(nextMonth) };
    },
  },

  // Quarterly periods
  {
    label: "This quarter",
    value: "thisquarter",
    description: "First to last day of current quarter",
    getDateRange: () => {
      const now = new Date();
      return { start: startOfQuarter(now), end: endOfQuarter(now) };
    },
  },
  {
    label: "Last quarter",
    value: "lastquarter",
    description: "First to last day of previous quarter",
    getDateRange: () => {
      const lastQuarter = subQuarters(new Date(), 1);
      return { start: startOfQuarter(lastQuarter), end: endOfQuarter(lastQuarter) };
    },
  },
  {
    label: "Next quarter",
    value: "nextquarter",
    description: "First to last day of next quarter",
    getDateRange: () => {
      const nextQuarter = addQuarters(new Date(), 1);
      return { start: startOfQuarter(nextQuarter), end: endOfQuarter(nextQuarter) };
    },
  },

  // Yearly periods
  {
    label: "This year",
    value: "thisyear",
    description: "January 1st to December 31st of current year",
    getDateRange: () => {
      const now = new Date();
      return { start: startOfYear(now), end: endOfYear(now) };
    },
  },
  {
    label: "Last year",
    value: "lastyear",
    description: "January 1st to December 31st of previous year",
    getDateRange: () => {
      const lastYear = subYears(new Date(), 1);
      return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
    },
  },
  {
    label: "Next year",
    value: "nextyear",
    description: "January 1st to December 31st of next year",
    getDateRange: () => {
      const nextYear = addYears(new Date(), 1);
      return { start: startOfYear(nextYear), end: endOfYear(nextYear) };
    },
  },
];

/**
 * Get a specific preset by value
 */
export const getDatetimePreset = (value: string): DatetimePreset | undefined => {
  return getDatetimePresets().find((preset) => preset.value === value);
};

/**
 * Get date range for a preset value
 */
export const getDateRangeForPreset = (value: string): { start: Date; end: Date } | null => {
  const preset = getDatetimePreset(value);
  return preset ? preset.getDateRange() : null;
};

/**
 * Check if a value is a custom range (stored as JSON)
 */
export const isCustomRange = (value: string): boolean => {
  return value.startsWith("custom:");
};

/**
 * Parse custom range value
 */
export const parseCustomRange = (value: string): { start: Date; end: Date } | null => {
  if (!isCustomRange(value)) return null;

  try {
    const rangeData = JSON.parse(value.replace("custom:", ""));
    return {
      start: new Date(rangeData.start),
      end: new Date(rangeData.end),
    };
  } catch {
    return null;
  }
};

/**
 * Create custom range value
 */
export const createCustomRange = (start: Date, end: Date): string => {
  return `custom:${JSON.stringify({ start: start.toISOString(), end: end.toISOString() })}`;
};

/**
 * Get display label for a filter value
 */
export const getFilterDisplayLabel = (value: string): string => {
  if (value === NEVER_LAST_ACTIVITY_PRESET_VALUE) {
    return NEVER_LAST_ACTIVITY_PRESET_LABEL;
  }

  if (isCustomRange(value)) {
    const range = parseCustomRange(value);
    if (range) {
      return `Custom: ${format(range.start, "MMM d")} - ${format(range.end, "MMM d, yyyy")}`;
    }
    return "Custom range";
  }

  const preset = getDatetimePreset(value);
  return preset ? preset.label : value;
};
